import React, { useState, useEffect, useCallback } from 'react';
import {
  AiOutlinePlus,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineSearch,
  AiOutlineFilter,
  AiOutlineSortAscending,
  AiOutlineSortDescending,
  AiOutlineSave,
  AiOutlineClose,
  AiOutlineReload,
  AiOutlineAppstore,
  AiOutlineUnorderedList,
} from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import useFonts from '../hooks/useFonts';
import { useNotification } from '../hooks/useNotification';
import { formatDate as formatDateUtil } from '../utils/dateUtils';
import { useAclService } from '../services/api';
import { useConfirm } from '../components/ui/ConfirmProvider';
import AclContextualView from '../components/AclContextualView/AclContextualView';
import AclImpactWarning from '../components/AclContextualView/AclImpactWarning';
import FolderTreePicker from '../components/FolderTreePicker/FolderTreePicker';

const AclManagement = ({ onBack }) => {
  const navigate = useNavigate();
  const aclService = useAclService();
  const { fonts } = useFonts();
  const notify = useNotification();
  const confirm = useConfirm();

  // Estados principales
  const [acls, setAcls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados para filtros y ordenamiento
  const [filters, setFilters] = useState({
    // resourceType eliminado de la UI: hoy sólo se usa 'FOLDER' (cualquier nodo
    // del árbol) — el campo se mantiene oculto en el formData y hardcoded al
    // enviar al backend.
    subjectType: '',
    effect: '',
    search: ''
  });
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');

  // Estados para modal de creación/edición
  const [showModal, setShowModal] = useState(false);
  const [editingAcl, setEditingAcl] = useState(null);
  const [formData, setFormData] = useState({
    resourceType: 'FOLDER',
    resourceId: '',
    subjectType: 'GROUP',
    subjectId: '',
    effect: 'ALLOW',
    timeFrom: '',
    timeTo: '',
    ipRanges: '',
    actions: []
  });

  // Estados para acciones
  const [selectedActions, setSelectedActions] = useState([]);
  //const [availableActions] = useState(['LIST', 'CREATE_DOCUMENT', 'ADMIN']);
  const [availableActions] = useState(['LIST']);

  // Estado para series documentales (tipos de documentos)
  const [seriesDocumentales, setSeriesDocumentales] = useState([]);
  const [loadingSeries, setLoadingSeries] = useState(false);

  // ─── Vista contextual (#57 follow-up) ──────────────────────────────────
  // 'contextual' = árbol + panel por nodo (default). 'flat' = tabla legacy.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('boveda.acl.viewMode') || 'contextual'; }
    catch { return 'contextual'; }
  });
  const switchView = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('boveda.acl.viewMode', mode); } catch {}
  };

  // Folders + audit por nodo seleccionado.
  const [folders, setFolders] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // Impact warning antes de submit.
  const [impactOpen, setImpactOpen] = useState(false);
  const [impactData, setImpactData] = useState(null);
  const [pendingOp, setPendingOp] = useState(null); // 'create'|'edit'|'delete'
  const [pendingPayload, setPendingPayload] = useState(null);

  // Resuelve el nombre legible de un nodo del árbol (folder o serie). Las
  // entries de ACL guardan resource_id con el id del nodo — la UI mostraba
  // antes "Serie documental"/getSeriesName, pero también hay ACLs en folders
  // (vía la vista contextual), así que necesitamos un lookup combinado.
  const getNodeName = (id) => {
    if (!id) return '';
    const serie = seriesDocumentales.find(s => s.id === id);
    if (serie) return `${serie.name} (${id})`;
    const folder = folders.find(f => f.id === id);
    if (folder) return `${folder.name} (${id})`;
    return id;
  };

  // Cargar ACLs y series al montar el componente
  useEffect(() => {
    cargarAcls();
    cargarSeriesDocumentales();
    cargarFolders();
  }, []);

  const cargarFolders = async () => {
    try {
      const response = await aclService.getFolders();
      setFolders(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.warn('Error cargando folders para la vista contextual:', err);
    }
  };

  // Carga del audit en función del nodo seleccionado.
  // No usamos useCallback porque `aclService`/`notify` se recrean cada render
  // (`useApiClient` y `useNotification` devuelven instancias nuevas), lo cual
  // generaba un loop: cada fallo del fetch → notify.error → re-render → nuevo
  // aclService → useCallback invalidado → useEffect re-dispara → fetch otra
  // vez. Inline + flag `cancelled` + dep única `selectedNodeId` evita el ping-pong.
  useEffect(() => {
    if (!selectedNodeId) { setAudit(null); return undefined; }
    let cancelled = false;
    setLoadingAudit(true);
    aclService.getAclAudit(selectedNodeId)
      .then((response) => { if (!cancelled) setAudit(response.data); })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error cargando audit ACL:', err);
        setAudit(null);
        notify.error(`No se pudo cargar la auditoría del nodo: ${err.response?.data?.detail || err.message}`);
      })
      .finally(() => { if (!cancelled) setLoadingAudit(false); });
    return () => { cancelled = true; };
    // aclService/notify se omiten a propósito (ver comentario arriba).
  }, [selectedNodeId]); // eslint-disable-line

  // Refresh imperativo tras create/edit/delete (no pasa por el useEffect).
  const refreshAudit = (nodeId) => {
    if (!nodeId) return;
    setLoadingAudit(true);
    aclService.getAclAudit(nodeId)
      .then((response) => setAudit(response.data))
      .catch((err) => {
        console.error('Error cargando audit ACL:', err);
      })
      .finally(() => setLoadingAudit(false));
  };

  // Confirmación con preview de impacto antes de aplicar create/edit/delete.
  const requireImpactConfirm = async (nodeId, op, payload) => {
    try {
      const response = await aclService.getAclImpact(nodeId);
      setImpactData(response.data);
      setPendingOp(op);
      setPendingPayload(payload);
      setImpactOpen(true);
    } catch (err) {
      console.error('Error obteniendo impact preview:', err);
      // Fallback: si falla el preview, seguir directo (no bloquear al admin).
      await runPendingOp(op, payload);
    }
  };

  const runPendingOp = async (op, payload) => {
    try {
      if (op === 'create') {
        await aclService.createAcl(payload);
        notify.success('ACL creada');
      } else if (op === 'edit') {
        const { id, ...data } = payload;
        await aclService.updateAcl(id, data);
        notify.success('ACL actualizada');
      } else if (op === 'delete') {
        await aclService.deleteAcl(payload.id);
        notify.success('ACL eliminada');
      }
      await cargarAcls();
      if (selectedNodeId) await refreshAudit(selectedNodeId);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      notify.error(`Error: ${detail}`);
    }
  };

  const onImpactConfirm = async () => {
    setImpactOpen(false);
    await runPendingOp(pendingOp, pendingPayload);
    setPendingOp(null);
    setPendingPayload(null);
  };

  // Handlers para la vista contextual (componente AclContextualView).
  const ctxOnCreate = (nodeId) => {
    setEditingAcl(null);
    setFormData({
      resourceType: 'FOLDER', resourceId: nodeId,
      subjectType: 'GROUP', subjectId: '', effect: 'ALLOW',
      timeFrom: '', timeTo: '', ipRanges: '', actions: [],
    });
    setSelectedActions(['LIST']);
    setShowModal(true);
  };
  const ctxOnEdit = (entry) => {
    // entry es AclEntryView del audit response; convertir al shape del modal viejo.
    setEditingAcl({ id: entry.id });
    setFormData({
      resourceType: 'FOLDER',
      resourceId: entry.sourceNodeId,
      subjectType: entry.subjectType,
      subjectId: entry.subjectId,
      effect: entry.effect,
      timeFrom: entry.timeFrom ? entry.timeFrom.split('T')[0] : '',
      timeTo: entry.timeTo ? entry.timeTo.split('T')[0] : '',
      ipRanges: '',
      actions: entry.actions || [],
    });
    setSelectedActions(entry.actions || []);
    setShowModal(true);
  };
  const ctxOnDelete = async (entry) => {
    await requireImpactConfirm(entry.sourceNodeId, 'delete', { id: entry.id });
  };
  const ctxOnNavigateAncestor = (nodeId) => setSelectedNodeId(nodeId);

  const cargarAcls = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await aclService.listAcls();
      setAcls(response.data.acls || []);
    } catch (err) {
      console.error('Error cargando ACLs:', err);
      setError('Error al cargar las ACLs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const cargarSeriesDocumentales = async () => {
    try {
      setLoadingSeries(true);
      const response = await aclService.getTiposDocumentos();
      setSeriesDocumentales(response.data || []);
    } catch (err) {
      console.error('Error cargando series documentales:', err);
      setSeriesDocumentales([]);
    } finally {
      setLoadingSeries(false);
    }
  };

  const handleCreate = () => {
    setEditingAcl(null);
    setFormData({
      resourceType: 'FOLDER',
      resourceId: '',
      subjectType: 'GROUP',
      subjectId: '',
      effect: 'ALLOW',
      timeFrom: '',
      timeTo: '',
      ipRanges: '',
      actions: []
    });
    setSelectedActions(['LIST']);
    cargarSeriesDocumentales();
    setShowModal(true);
  };

  const handleEdit = (acl) => {
    setEditingAcl(acl);
    setFormData({
      resourceType: acl.resourceType,
      resourceId: acl.resourceId,
      subjectType: acl.subjectType,
      subjectId: acl.subjectId,
      effect: acl.effect,
      timeFrom: acl.timeFrom ? acl.timeFrom.split('T')[0] : '',
      timeTo: acl.timeTo ? acl.timeTo.split('T')[0] : '',
      ipRanges: acl.ipRanges || '',
      actions: acl.actions || []
    });
    setSelectedActions(acl.actions || []);
    cargarSeriesDocumentales();
    setShowModal(true);
  };

  const handleDelete = async (aclId) => {
    const ok = await confirm({
      title: 'Eliminar ACL',
      message: '¿Estás seguro de eliminar esta ACL?',
      confirmLabel: 'Eliminar',
      destructive: true,
    });
    if (!ok) return;
    try {
      await aclService.deleteAcl(aclId);
      await cargarAcls();
      notify.success('ACL eliminada exitosamente');
    } catch (err) {
      console.error('Error eliminando ACL:', err);
      notify.error('Error al eliminar la ACL');
    }
  };

  const isFormValid = () => {
    return (
      formData.subjectId.trim() !== '' &&
      formData.resourceId.trim() !== '' &&
      selectedActions.length > 0
    );
  };

  const handleSave = async () => {
    const aclData = {
      ...formData,
      actions: selectedActions,
      timeFrom: formData.timeFrom ? `${formData.timeFrom}T00:00:00` : null,
      timeTo: formData.timeTo ? `${formData.timeTo}T23:59:59` : null,
      ipRanges: formData.ipRanges || null
    };

    setShowModal(false);

    // Cualquier create/edit dispara el warning de impacto previo (muestra
    // cuántas series descendientes serán reindexadas en cascada).
    if (editingAcl) {
      await requireImpactConfirm(formData.resourceId, 'edit', { id: editingAcl.id, ...aclData });
    } else {
      await requireImpactConfirm(formData.resourceId, 'create', aclData);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleAction = (action) => {
    setSelectedActions(prev =>
      prev.includes(action)
        ? prev.filter(a => a !== action)
        : [...prev, action]
    );
  };

  // Filtrar y ordenar ACLs
  const filteredAcls = acls
    .filter(acl => {
      if (filters.subjectType && acl.subjectType !== filters.subjectType) return false;
      if (filters.effect && acl.effect !== filters.effect) return false;
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        return (
          acl.resourceId.toLowerCase().includes(searchLower) ||
          acl.subjectId.toLowerCase().includes(searchLower) ||
          acl.actions.some(action => action.toLowerCase().includes(searchLower))
        );
      }
      return true;
    })
    .sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <AiOutlineSortAscending /> : <AiOutlineSortDescending />;
  };

  const formatDate = (dateString) => formatDateUtil(dateString);

  // Mapeo de acciones técnicas → etiquetas amigables (solo LIST hoy; otras reservadas)
  const ACTION_LABELS = { LIST: 'Listar', READ: 'Leer', WRITE: 'Escribir', DELETE: 'Eliminar' };
  const SUBJECT_LABELS = { GROUP: 'Grupo', USER: 'Usuario' };
  // RESOURCE_LABELS: vestigial. El campo resource_type queda hardcodeado a
  // 'FOLDER' (todos los nodos del árbol comparten ese resource_type) — la UI
  // no lo expone más. Si en el futuro se implementan ACLs a nivel DOCUMENT,
  // se reintroduce.

  const formatActions = (actions) => {
    return (actions || []).map(a => ACTION_LABELS[a] || a).join(', ');
  };
  const formatSubjectType = (t) => SUBJECT_LABELS[t] || t;

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.titleSection}>
            <AiOutlineFilter style={styles.titleIcon} />
            <h1 style={styles.title}>Gestión de ACLs</h1>
          </div>
          <div style={styles.headerActions}>
            <button style={styles.backButton} onClick={onBack}>
              <AiOutlineClose style={styles.backIcon} />
              Cerrar
            </button>
          </div>
        </div>
        <div style={styles.loading}>Cargando ACLs...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.titleSection}>
          <AiOutlineFilter style={styles.titleIcon} />
          <h1 style={styles.title}>Gestión de ACLs</h1>
        </div>
        <div style={styles.headerActions}>
          {/* Toggle vista contextual (default, con herencia) vs vista plana (legacy, auditoría global). */}
          <div role="tablist" style={tabsStyles.tabs}>
            <button
              role="tab"
              aria-selected={viewMode === 'contextual'}
              onClick={() => switchView('contextual')}
              style={{ ...tabsStyles.tab, ...(viewMode === 'contextual' ? tabsStyles.tabActive : {}) }}
              data-testid="acl-view-contextual"
            >
              <AiOutlineAppstore /> Por nodo
            </button>
            <button
              role="tab"
              aria-selected={viewMode === 'flat'}
              onClick={() => switchView('flat')}
              style={{ ...tabsStyles.tab, ...(viewMode === 'flat' ? tabsStyles.tabActive : {}) }}
              data-testid="acl-view-flat"
            >
              <AiOutlineUnorderedList /> Tabla global
            </button>
          </div>
          <button
            style={{
              ...styles.createButton,
              ...(viewMode !== 'flat' ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
            }}
            onClick={viewMode === 'flat' ? handleCreate : undefined}
            disabled={viewMode !== 'flat'}
            title={viewMode === 'flat'
              ? 'Crear una nueva ACL'
              : 'En la vista "Por nodo": seleccioná un folder o serie en el árbol y usá "+ Nueva ACL local"'}
          >
            <AiOutlinePlus style={styles.buttonIcon} />
            Nueva ACL
          </button>
          <button style={styles.backButton} onClick={onBack}>
            <AiOutlineClose style={styles.backIcon} />
            Cerrar
          </button>
        </div>
      </div>

      {viewMode === 'contextual' && (
        <AclContextualView
          folders={folders}
          series={seriesDocumentales}
          audit={audit}
          loading={loadingAudit}
          selectedNodeId={selectedNodeId}
          onSelectNode={setSelectedNodeId}
          onCreate={ctxOnCreate}
          onEdit={ctxOnEdit}
          onDelete={ctxOnDelete}
          onNavigateToAncestor={ctxOnNavigateAncestor}
        />
      )}

      {viewMode === 'flat' && (
        <>
      {/* Filtros */}
      <div style={styles.filtersContainer}>
        <div style={styles.filtersRow}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Buscar:</label>
            <input
              type="text"
              placeholder="Buscar por recurso, sujeto o acción..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              style={styles.searchInput}
            />
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Destinatario:</label>
            <select
              value={filters.subjectType}
              onChange={(e) => handleFilterChange('subjectType', e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">Todos</option>
              <option value="GROUP">Grupo</option>
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Efecto:</label>
            <select
              value={filters.effect}
              onChange={(e) => handleFilterChange('effect', e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">Todos</option>
              <option value="ALLOW">ALLOW</option>
              <option value="DENY">DENY</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabla de ACLs */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.tableHeaderCell} onClick={() => handleSort('id')}>
                <div style={styles.headerContent}>
                  <span>ID</span>
                  {getSortIcon('id')}
                </div>
              </th>
              <th style={styles.tableHeaderCell} onClick={() => handleSort('subjectType')}>
                <div style={styles.headerContent}>
                  <span>Destinatario</span>
                  {getSortIcon('subjectType')}
                </div>
              </th>
              <th style={styles.tableHeaderCell} onClick={() => handleSort('subjectId')}>
                <div style={styles.headerContent}>
                  <span>ID</span>
                  {getSortIcon('subjectId')}
                </div>
              </th>
              <th style={styles.tableHeaderCell} onClick={() => handleSort('resourceId')}>
                <div style={styles.headerContent}>
                  <span>Folder / Serie</span>
                  {getSortIcon('resourceId')}
                </div>
              </th>
              <th style={styles.tableHeaderCell} onClick={() => handleSort('effect')}>
                <div style={styles.headerContent}>
                  <span>Efecto</span>
                  {getSortIcon('effect')}
                </div>
              </th>
              {/*<th style={styles.tableHeaderCell}>Válido Desde</th>
              <th style={styles.tableHeaderCell}>Válido Hasta</th>
              <th style={styles.tableHeaderCell}>IPs</th>*/}
              <th style={styles.tableHeaderCell}>Acciones</th>
              <th style={styles.tableHeaderCell}>Operaciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredAcls.map(acl => (
              <tr key={acl.id} style={styles.tableRow}>
                <td style={styles.tableCell}>{acl.id}</td>
                <td style={styles.tableCell}>{formatSubjectType(acl.subjectType)}</td>
                <td style={styles.tableCell}>{acl.subjectId}</td>
                <td style={styles.tableCell}>{getNodeName(acl.resourceId)}</td>
                <td style={styles.tableCell}>
                  <span style={{
                    ...styles.effectBadge,
                    backgroundColor: acl.effect === 'ALLOW' ? '#10B981' : '#EF4444'
                  }}>
                    {acl.effect}
                  </span>
                </td>
                {/*<td style={styles.tableCell}>{formatDate(acl.timeFrom)}</td>
                <td style={styles.tableCell}>{formatDate(acl.timeTo)}</td>
                <td style={styles.tableCell}>{acl.ipRanges || '-'}</td>*/}
                <td style={styles.tableCell}>{formatActions(acl.actions)}</td>
                <td style={styles.tableCell}>
                  <div style={styles.actionsContainer}>
                    <button
                      style={styles.editButton}
                      onClick={() => handleEdit(acl)}
                      title="Editar ACL"
                    >
                      <AiOutlineEdit />
                    </button>
                    <button
                      style={styles.deleteButton}
                      onClick={() => handleDelete(acl.id)}
                      title="Eliminar ACL"
                    >
                      <AiOutlineDelete />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredAcls.length === 0 && (
          <div style={styles.noData}>
            No se encontraron ACLs con los filtros aplicados
          </div>
        )}
      </div>
      </>
      )}

      <AclImpactWarning
        open={impactOpen}
        impact={impactData}
        operation={pendingOp}
        onConfirm={onImpactConfirm}
        onCancel={() => { setImpactOpen(false); setPendingOp(null); setPendingPayload(null); }}
      />

      {/* Modal de Creación/Edición */}
      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingAcl ? 'Editar ACL' : 'Nueva ACL'}
              </h2>
              <button
                style={styles.closeButton}
                onClick={() => setShowModal(false)}
              >
                <AiOutlineClose />
              </button>
            </div>

            <div style={styles.modalContent}>
              <p style={{
                margin: '0 0 16px 0',
                padding: '10px 12px',
                background: '#DBEAFE',
                border: '1px solid #93C5FD',
                borderRadius: 6,
                fontSize: 13,
                color: '#1E3A8A',
                lineHeight: 1.4,
              }}>
                Una ACL concede a un <strong>grupo</strong> permiso para <strong>listar</strong> los documentos
                de una <strong>serie documental</strong> (y sus sub-series).
              </p>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Destinatario:</label>
                  <select
                    value={formData.subjectType}
                    onChange={(e) => setFormData({ ...formData, subjectType: e.target.value })}
                    style={styles.formSelect}
                  >
                    <option value="GROUP">Grupo</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>ID del grupo:</label>
                  <input
                    type="text"
                    value={formData.subjectId}
                    onChange={(e) => setFormData({ ...formData, subjectId: e.target.value })}
                    style={styles.formInput}
                    placeholder="ej: finance-team"
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Folder / Serie:</label>
                  {/* Picker con árbol completo: folders + series. Cualquiera
                      es seleccionable. Una ACL en un folder se hereda a todos
                      sus descendientes (semántica A.2 — ⋃ALLOW − ⋃DENY). */}
                  <FolderTreePicker
                    folders={folders}
                    series={seriesDocumentales}
                    allowSeries
                    value={formData.resourceId}
                    onChange={(id) => setFormData({ ...formData, resourceId: id })}
                    placeholder="Seleccionar folder o serie…"
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Efecto:</label>
                  <select
                    value={formData.effect}
                    onChange={(e) => setFormData({ ...formData, effect: e.target.value })}
                    style={styles.formSelect}
                  >
                    <option value="ALLOW">ALLOW</option>
                    <option value="DENY">DENY</option>
                  </select>
                </div>

                {/*<div style={styles.formGroup}>
                  <label style={styles.formLabel}>Rangos de IP:</label>
                  <input
                    type="text"
                    value={formData.ipRanges}
                    onChange={(e) => setFormData({ ...formData, ipRanges: e.target.value })}
                    style={styles.formInput}
                    placeholder="ej: 192.168.1.0/24"
                  />
                </div>*/}
              </div>

              {/*<div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Válido Desde:</label>
                  <input
                    type="date"
                    value={formData.timeFrom}
                    onChange={(e) => setFormData({ ...formData, timeFrom: e.target.value })}
                    style={styles.formInput}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Válido Hasta:</label>
                  <input
                    type="date"
                    value={formData.timeTo}
                    onChange={(e) => setFormData({ ...formData, timeTo: e.target.value })}
                    style={styles.formInput}
                  />
                </div>
              </div>*/}

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Acciones:</label>
                <div style={styles.actionsContainer}>
                  {availableActions.map(action => (
                    <label key={action} style={styles.actionCheckbox}>
                      <input
                        type="checkbox"
                        checked={selectedActions.includes(action)}
                        onChange={() => toggleAction(action)}
                      />
                      <span style={styles.actionLabel}>{action}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                style={styles.cancelButton}
                onClick={() => setShowModal(false)}
              >
                <AiOutlineClose style={styles.buttonIcon} />
                Cancelar
              </button>
              <button
                style={{
                  ...styles.saveButton,
                  ...(isFormValid() ? {} : styles.disabledButton)
                }}
                onClick={handleSave}
                disabled={!isFormValid()}
              >
                <AiOutlineSave style={styles.buttonIcon} />
                {editingAcl ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#F8F9FA',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 16px',
    backgroundColor: '#6B7280',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s ease',
  },
  backIcon: {
    fontSize: '16px',
  },
  titleIcon: {
    fontSize: '28px',
    color: '#00468B',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#00468B',
  },
  createButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#10B981',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  filtersContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: '6px',
    padding: '12px 16px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  filtersRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
  },
  searchInput: {
    padding: '6px 10px',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    fontSize: '13px',
    height: '32px',
  },
  filterSelect: {
    padding: '6px 10px',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    fontSize: '13px',
    height: '32px',
  },
  tableContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#F9FAFB',
  },
  tableHeaderCell: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    fontSize: '14px',
    color: '#374151',
    borderBottom: '1px solid #E5E7EB',
    cursor: 'pointer',
    userSelect: 'none',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  tableRow: {
    borderBottom: '1px solid #F3F4F6',
    transition: 'background-color 0.2s ease',
  },
  tableCell: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#374151',
  },
  effectBadge: {
    padding: '4px 8px',
    borderRadius: '4px',
    color: '#FFFFFF',
    fontSize: '12px',
    fontWeight: '500',
  },
  actionsContainer: {
    display: 'flex',
    gap: '8px',
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    backgroundColor: '#3B82F6',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.2s ease',
  },
  deleteButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    backgroundColor: '#EF4444',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.2s ease',
  },
  noData: {
    textAlign: 'center',
    padding: '40px',
    color: '#6B7280',
    fontSize: '16px',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#6B7280',
    fontSize: '16px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #E5E7EB',
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#00468B',
  },
  closeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    color: '#6B7280',
  },
  modalContent: {
    padding: '20px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  formLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
  },
  formInput: {
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
  },
  formSelect: {
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
  },
  actionCheckbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  },
  actionLabel: {
    fontSize: '14px',
    color: '#374151',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '20px',
    borderTop: '1px solid #E5E7EB',
  },
  cancelButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#6B7280',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#10B981',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  disabledButton: {
    backgroundColor: '#9CA3AF',
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  buttonIcon: {
    fontSize: '16px',
  },
};

const tabsStyles = {
  tabs: {
    display: 'inline-flex',
    background: '#F3F4F6',
    borderRadius: '8px',
    padding: '4px',
    marginRight: '12px',
  },
  tab: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#6B7280',
  },
  tabActive: {
    background: '#FFFFFF',
    color: '#00468B',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    fontWeight: '600',
  },
};

export default AclManagement;
